import { Injectable } from '@angular/core';
import {
  forEach as _forEach,
  isNil as _isNil,
  map as _map,
  cloneDeep as _cloneDeep,
  assign as _assign,
  padStart as _padStart,
  padEnd as _padEnd,
  isEmpty as _isEmpty,
} from 'lodash';
import * as _moment from 'moment';
import {
  DateTimeUtils, DateTimeOutput, StringUtils,
  DateTimePadding, DateSeparator, DateEra
} from '@blcloud/bl-common';
import { BlConditionType, BlConditionInfo, BlListResponseBody } from '@blcloud/bl-ng-common';
import {
  ProfileEntryResource,
  ProfileBulkMainEntryReqResource,
  ProfileEntryCustomerVehicleLinkReqResource,
  ProfileEntryCustomerVehicleLinkOptResource,
} from '@blcloud/bl-ng-resource';
import {
  IProfileEntry,
  IProfileBulkMainEntryReqDtl,
  IProfileBulkMainEntryReq,
  IProfileEntryCustomerVehicleLinkReq,
  IProfileEntryCustomerVehicleLinkReqDtl,
  IProfileEntryCustomerVehicleLink,
  IProfileEntryCustomerVehicleLinkOpt,
} from '@blcloud/bl-datamodel';
import { LoginResourceService } from '@blcloud/bl-ng-share-module';
import { Observable } from 'rxjs/Observable';
import { map } from 'rxjs/operators';
import { LogicalDeleteDiv } from '@blcloud/bl-datamodel/enum/common/logical-delete-div';
import { IDispProfileEntry, IDetailCondition } from './profile-entries-page.interface';
import { of as ObservableOf } from 'rxjs/observable/of';
import { ProfileConst } from '@blcloud/bl-datamodel/const/profile';
import { CustomerInfoReflectStatusDiv } from '@blcloud/bl-datamodel/enum/customer/customer-info-reflect-status-div';
import { CompanyConst } from '@blcloud/bl-datamodel/const/company';
import { RoleConst } from '@blcloud/bl-datamodel/const/role';
import { SexDivArray } from '@blcloud/bl-datamodel/enum/common/sex-div';

/**
 * プロフィール登録一覧画面 ロジックサービス
 */
@Injectable()
export class ProfileEntriesPageService {

  /** 1ページの件数 */
  SEARCH_PAGE_SIZE = 10;

  constructor(
    private loginResourceService: LoginResourceService,
    private profileEntryResource: ProfileEntryResource,
    private profileBulkMainEntryReqResource: ProfileBulkMainEntryReqResource,
    private profileEntryCustomerVehicleLinkReqResource: ProfileEntryCustomerVehicleLinkReqResource,
    private profileEntryCustomerVehicleLinkOptResource: ProfileEntryCustomerVehicleLinkOptResource,
  ) {
  }

  /**
   * プロフィール登録情報検索実行
   * @param organizationCode 組織コード
   * @param page ページ
   * @param reflectSearch 反映済み検索フラグ
   * @return 検索結果ストリーム
   */
  getProfileEntries(
    organizationCode: string,
    page: number, reflectSearch: boolean,
    detailCondition: IDetailCondition,
  ): Observable<BlListResponseBody<IDispProfileEntry>> {
    const condition: BlConditionInfo[] = [
      { key: 'logicalDeleteDiv', value: LogicalDeleteDiv.Valid, type: BlConditionType.Equal },
    ];
    // ソート条件
    const sortFields = [];
    // 組織
    if (organizationCode !== CompanyConst.ORGANIZATION_CODE_ALL_COMPANY) {
      condition.push({
        key: 'organizationCode',
        value: organizationCode,
        type: BlConditionType.Equal
      });
    }
    if (reflectSearch) {
      condition.push({
        key: 'customerInfoReflectStatusDiv',
        value: [CustomerInfoReflectStatusDiv.Reflected, CustomerInfoReflectStatusDiv.Discard],
        type: BlConditionType.In
      });
      sortFields.push({ field: 'custRefStsUpdDateTime', desc: true });
    } else {
      condition.push({
        key: 'customerInfoReflectStatusDiv',
        value: CustomerInfoReflectStatusDiv.NotReflected,
        type: BlConditionType.Equal
      });
      condition.push({
        key: 'sysInternalLabelList',
        value: ProfileConst.PROFILE_LATEST,
        type: BlConditionType.Equal
      });
      sortFields.push({ field: 'profileRegistDateTime', desc: true });
    }
    const searchParam = {
      conditions: this.createDetailSearchParams(condition, detailCondition),
      sortFields,
      size: this.SEARCH_PAGE_SIZE,
      page
    };
    return this.profileEntryResource.search<IProfileEntry>(searchParam).pipe(
      map(
        response => {
          if (_isNil(response)) {
            return null;
          }
          response.searchResultList = this.convert(response.searchResultList) || [];
          return response;
        }
      )
    );
  }

  /**
   * 詳細条件の検索条件を設定します。
   * @param condition
   * @returns 検索条件情報
   */
  private createDetailSearchParams(condition: BlConditionInfo[], detailCondition: IDetailCondition): BlConditionInfo[][] {
    const conditions: BlConditionInfo[][] = [];
    // 顧客フリガナ
    if (!_isEmpty(detailCondition.customerNameKana1)) {
      condition.push({
        key: 'customerNameKana1',
        value: detailCondition.customerNameKana1,
        type: BlConditionType.Match
      });
    }
    // 連絡先情報(電話)
    if (!_isEmpty(detailCondition.contactInfoTel)) {
      condition.push({
        key: 'contactInfoTel.contactInformation',
        value: detailCondition.contactInfoTel,
        type: BlConditionType.Match
      });
    }
    // 連絡先情報(メール)
    if (!_isEmpty(detailCondition.contactInfoEmail)) {
      condition.push({
        key: 'contactInfoEmail.contactInformation',
        value: detailCondition.contactInfoEmail,
        type: BlConditionType.Match
      });
    }
    // ナンバープレート（下4桁）
    if (!_isEmpty(detailCondition.numPlateSeriesSpecifNum)) {
      condition.push({
        key: 'profileEntryVehicleList.numPlateSeriesSpecifNum',
        value: detailCondition.numPlateSeriesSpecifNum,
        type: BlConditionType.Equal
      });
    }
    // プロフィール登録日時（開始日）
    if (this.isValid(detailCondition.profileRegistDateTimeStart)) {
      condition.push({
        key: 'profileRegistDateTime',
        value: _moment(String(detailCondition.profileRegistDateTimeStart)).startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
        type: BlConditionType.GreaterThanEqual,
      });
    }
    // プロフィール登録日時（終了日）
    if (this.isValid(detailCondition.profileRegistDateTimeEnd)) {
      condition.push({
        key: 'profileRegistDateTime',
        value: _moment(String(detailCondition.profileRegistDateTimeEnd)).endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
        type: BlConditionType.LessThanEqual,
      });
    }
    // 本人認証連絡先がある場合は電話番号とメールアドレスで2個分をOR検索する
    if (!_isEmpty(detailCondition.useAuthContact)) {
      const conditionTel = _cloneDeep(condition);
      const conditionEmail = _cloneDeep(condition);
      conditionTel.push({
        key: 'phoneNumber',
        value: detailCondition.useAuthContact,
        type: BlConditionType.Match
      });
      conditions.push(conditionTel);
      conditionEmail.push({
        key: 'emailAddress',
        value: detailCondition.useAuthContact,
        type: BlConditionType.Match
      });
      conditions.push(conditionEmail);
    } else {
      conditions.push(condition);
    }
    return conditions;
  }

  /**
   * 検索結果を構成する
   *
   * @param response 返却データ
   * @return 加工済み返却データ
   */
  private convert(response: IProfileEntry[]): IDispProfileEntry[] {
    const setDataList: IDispProfileEntry[] = [];
    if (_isNil(response)) {
      return setDataList;
    }
    _map(_cloneDeep(response), profileEntry => {
      let setData: IDispProfileEntry = {};
      setData = _assign({}, profileEntry);
      setData.holdProfileEntry = profileEntry;
      // エンティティの値を加工して表示値をセット
      // 仮登録日時
      if (profileEntry.profileRegistDateTime) {
        setData.profileRegistDateTime = DateTimeUtils.format(
          DateTimeUtils.parseDate(profileEntry.profileRegistDateTime),
          DateTimeOutput.YMD_LT, DateTimePadding.ZERO, DateSeparator.JP, DateEra.JP
        );
      }
      // 顧客情報反映日
      if (profileEntry.customerInfoReflectDate) {
        setData.dispCustomerInfoReflectDate = DateTimeUtils.format(
          profileEntry.customerInfoReflectDate,
          DateTimeOutput.YMD, DateTimePadding.NONE, DateSeparator.JP, DateEra.JP
        );
      }
      if (profileEntry.customerInfoReflectStatusDiv === CustomerInfoReflectStatusDiv.Discard) {
        setData.dispCustomerInfoReflectDate = '破棄';
      }
      // 顧客名
      if (profileEntry.customerFamilyName || profileEntry.customerGivenName) {
        setData.customerName = StringUtils.concatWith(
          '　',
          profileEntry.customerFamilyName,
          profileEntry.customerGivenName
        );
      }
      // 顧客名カナ
      if (profileEntry.customerFamilyNameKana || profileEntry.customerGivenNameKana) {
        setData.customerNameKana = StringUtils.concatWith(
          '　',
          profileEntry.customerFamilyNameKana,
          profileEntry.customerGivenNameKana
        );
      }
      // 性別
      setData.dispSexDiv = SexDivArray.filter(r => {
        return r.value === profileEntry.sexDiv;
      })[0].text;
      // 本人認証連絡先(メールアドレスか電話番号どちらかはあるはず)
      setData.useAuthContact = profileEntry.emailAddress ? profileEntry.emailAddress : profileEntry.phoneNumber;
      // 住所
      const customerAddressStateCity = profileEntry.customerAddressStateCity ? profileEntry.customerAddressStateCity : '';
      const customerAddressStreet = profileEntry.customerAddressStreet ? profileEntry.customerAddressStreet : '';
      const customerAddressBuildingName = profileEntry.customerAddressBuildingName ? profileEntry.customerAddressBuildingName : '';
      setData.address = StringUtils.concat(
        customerAddressStateCity,
        customerAddressStreet,
        ' ',
        customerAddressBuildingName
      );
      // 生年月日
      if (profileEntry.birthDay) {
        setData.dispBirthDay = DateTimeUtils.format(
          profileEntry.birthDay,
          DateTimeOutput.YMD, DateTimePadding.NONE, DateSeparator.JP, DateEra.JP);
      }
      if (!_isEmpty(profileEntry.profileEntryVehicleList)) {
        _forEach(profileEntry.profileEntryVehicleList, profileEntryVehicle => {
          setData.profileEntryVehicle = profileEntryVehicle;
          // ナンバープレート
          if (_isNil(profileEntryVehicle.vcleInspRegistOfficeName)
            && _isNil(profileEntryVehicle.numPlateClassNum)
            && _isNil(profileEntryVehicle.numPlateKana)
            && profileEntryVehicle.numPlateSeriesSpecifNum === 0
          ) {
            setData.numberPlate = '';
          } else if (profileEntryVehicle.numPlateSeriesSpecifNum === 0) {
            setData.numberPlate = '仮登録車両';
          } else {
            setData.numberPlate = StringUtils.concat(
              _padEnd(profileEntryVehicle.vcleInspRegistOfficeName, 4, '　'),
              ' ',
              _padEnd(profileEntryVehicle.numPlateClassNum, 3, ' '),
              ' ',
              _padEnd(profileEntryVehicle.numPlateKana, 1, '　'),
              ' ',
              _padStart(profileEntryVehicle.numPlateSeriesSpecifNum.toString(), 4, ' ')
            );
          }
          // メーカー／車種
          if (profileEntryVehicle.blVehicleMakerFullWFixNm || profileEntryVehicle.vehicleModelFixFullWName) {
            setData.makerModel = StringUtils.concatWith(
              '／',
              profileEntryVehicle.blVehicleMakerFullWFixNm,
              profileEntryVehicle.vehicleModelFixFullWName
            );
          }
          // 車検満期日
          if (profileEntryVehicle.vcleInspDueDate) {
            setData.dispVcleInspDueDate = DateTimeUtils.format(
              profileEntryVehicle.vcleInspDueDate,
              DateTimeOutput.YMD, DateTimePadding.NONE, DateSeparator.JP, DateEra.JP
            );
          }
          setDataList.push(setData);
        });
      } else {
        setData.numberPlate = '';
        setData.profileEntryVehicle = {};
        setDataList.push(setData);
      }
    });
    return setDataList;
  }

  /**
   * 顧客車両紐付け候補リクエスト
   * @param resultList 選択データリスト
   * @param reflectSearch 反映済み検索フラグ
   */
  getCustomerVehicleLinkReq(resultList: IDispProfileEntry[], reflectSearch: boolean): Observable<IProfileEntryCustomerVehicleLink> {
    // 反映済みのデータを検索する時は紐付け検索はしない
    if (reflectSearch) {
      return ObservableOf({});
    }
    const setDataList: IProfileEntryCustomerVehicleLinkReqDtl[] = [];
    _forEach(resultList, profileEntry => {
      const setData: IProfileEntryCustomerVehicleLinkReqDtl = {
        contactInfoEmail: profileEntry.contactInfoEmail,
        contactInfoTel: profileEntry.contactInfoTel,
        customerFamilyNameKana: profileEntry.customerFamilyNameKana,
        customerGivenNameKana: profileEntry.customerGivenNameKana,
        vcleInspRegistOfficeName: profileEntry.profileEntryVehicle.vcleInspRegistOfficeName,
        numPlateClassNum: profileEntry.profileEntryVehicle.numPlateClassNum,
        numPlateKana: profileEntry.profileEntryVehicle.numPlateKana,
        numPlateSeriesSpecifNum: profileEntry.profileEntryVehicle.numPlateSeriesSpecifNum,
        profileRegistNum: profileEntry.profileRegistNum,
        registerYearMonth: profileEntry.registerYearMonth,
        customerCode: profileEntry.customerCode,
        vehicleMgtNumber: profileEntry.profileEntryVehicle.vehicleMgtNumber,
        visitVehicleMgtNumber: profileEntry.visitVehicleMgtNumber,
        postalCode: profileEntry.postalCode,
        customerAddressStateCity: profileEntry.customerAddressStateCity,
        customerAddressStreet: profileEntry.customerAddressStreet,
        customerAddressBuildingName: profileEntry.customerAddressBuildingName,
      };
      setDataList.push(setData);
    });

    const body: IProfileEntryCustomerVehicleLinkReq = {
      profileEntryCustomerVehicleLinkReqDtlList: setDataList
    };
    return this.profileEntryCustomerVehicleLinkReqResource.post<IProfileEntryCustomerVehicleLink>(
      body,
      {
        entityOptions: {
          exPath: 'linkexists'
        },
      },
    );
  }

  /**
   * 顧客紐付け候補取得
   * @param profileEntry プロフィール登録情報
   * @return 結果ストリーム
   */
  getProfileMatching(profileEntry: IProfileEntry): Observable<IProfileEntryCustomerVehicleLinkOpt> {
    const body: IProfileEntryCustomerVehicleLinkOpt = {
      profileRegistNum: profileEntry.profileRegistNum,
      registerYearMonth: profileEntry.registerYearMonth,
    };
    return this.profileEntryCustomerVehicleLinkOptResource.post<IProfileEntryCustomerVehicleLinkOpt>(
      body,
      {
        entityOptions: {
          exPath: 'linksearch'
        },
      },
    );
  }

  /**
   * 本登録リクエスト
   * @param selectedList 選択データリスト
   */
  toMainEntry(selectedList: IDispProfileEntry[]): Observable<any> {
    if (selectedList.length === 0) {
      return ObservableOf({});
    }
    const setDataList: IProfileBulkMainEntryReqDtl[] = [];

    _forEach(selectedList, profileEntry => {
      const setData: IProfileBulkMainEntryReqDtl = {
        registerYearMonth: profileEntry.registerYearMonth,
        profileRegistNum: profileEntry.profileRegistNum,
        profileEntryCustomerVehicleLinkOptExistsFlag: profileEntry.profileEntryCustomerVehicleLinkOptExistsFlag,
      };
      setDataList.push(setData);
    });

    const body: IProfileBulkMainEntryReq = {
      profileBulkMainEntryReqDtlList: setDataList
    };
    return this.profileBulkMainEntryReqResource.post<IProfileBulkMainEntryReq>(body);
  }

  /**
   * プロフィール登録情報の一括更新(破棄)
   * @entities ProfileEntry プロフィール登録情報
   */
  multiplyDiscard(entities: IProfileEntry[]): Observable<any> {
    const body: IProfileEntry[] = entities;
    return this.profileEntryResource.post<IProfileEntry>(
      body,
      {
        entityOptions: {
          exPath: 'discard'
        },
      },
    );
  }

  /**
   * 有効な値か判定する
   * @param value
   * @returns 有効の場合true
   */
  isValid(value: any) {
    if (_isNil(value)) {
      return false;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    } else {
      if (value === '') {
        return false;
      }
      if (value === 0) {
        return false;
      }
      if (value === '1900-01-01' || value === 19000101) {
        return false;
      }
      if (value === '1900-01-01T00:00:00' || value === 19000101000000) {
        return false;
      }
      if (value === '1900-01' || value === 190001) {
        return false;
      }
    }
    return true;
  }

  /**
   * プロフィール本登録のロールを取得
   */
  isAvailableProfileMainEntryRole(): Observable<boolean> {
    return this.loginResourceService.isAvailableFunction(RoleConst.ROLEID_FUNC_ALL_PROFILE_MAIN_ENTRY);
  }
}



// WEBPACK FOOTER //
// ./src/app/page/profile-entries-page/profile-entries-page.service.ts